import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import {
  Trophy, Calendar, Layers, MapPin, Mail, Phone, Radio, Medal,
  Users, ChevronRight, Send, ChevronLeft,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";

/* Brand tokens — aligned with LandingHome + mobile theme */
const NAVY = "#0B1D33";
const OFF_WHITE = "#F4F1EC";
const CREAM = "#F7F2E8";
const FAIRWAY = "#2E4834";
const FAIRWAY_ACCENT = "#3F6B4A";
const SUNBURST = "#E8C76A";
const STONE = "#8A867E";
const BURGUNDY = "#6E1F2B";
const FONT_STACK = "'Hanken Grotesk', system-ui, -apple-system, sans-serif";
const FONT_SCRIPT = "'Caveat', 'Dancing Script', cursive";

const LOCKUP_NAVY = "/cadii/brand/lockup_cadii_navy.png";
const BIRD_WALKER = "/cadii/mascots/bird_walker_navy.png";
const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";

const NAV_ITEMS = [
  { k: "club", label: "Club" },
  { k: "jugadores", label: "Jugadores" },
  { k: "torneos", label: "Torneos" },
  { k: "contacto", label: "Contacto" },
];

const STATUS_MAP = {
  active: { label: "En curso", color: FAIRWAY, bg: "rgba(46,72,52,0.10)" },
  completed: { label: "Finalizado", color: STONE, bg: "rgba(138,134,126,0.14)" },
};

function isPastDate(d) {
  if (!d || typeof d !== "string") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dt = new Date(d.slice(0, 10) + "T00:00:00");
  if (isNaN(dt.getTime())) return false;
  return dt < today;
}

function EventsTabs({ tab, setTab, pastCount }) {
  const base = "px-4 py-2 rounded-full text-[13px] font-bold transition-all";
  return (
    <div className="inline-flex gap-1 mb-6 p-1 rounded-full" style={{ backgroundColor: "rgba(11,29,51,0.06)" }}>
      <button
        type="button"
        onClick={() => setTab("active")}
        className={base}
        style={tab === "active" ? { backgroundColor: NAVY, color: OFF_WHITE } : { color: STONE }}
        data-testid="events-tab-active"
      >
        Activos / Próximos
      </button>
      <button
        type="button"
        onClick={() => setTab("past")}
        className={base}
        style={tab === "past" ? { backgroundColor: NAVY, color: OFF_WHITE } : { color: STONE }}
        data-testid="events-tab-past"
      >
        Pasados{pastCount ? ` (${pastCount})` : ""}
      </button>
    </div>
  );
}

export default function ClubPublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState("club");
  const [eventsTab, setEventsTab] = useState("active");

  useEffect(() => {
    const load = async () => {
      try {
        const [clubRes, trnRes, playersRes] = await Promise.all([
          fetch(`${API}/public/clubs/${slug}`),
          fetch(`${API}/public/clubs/${slug}/tournaments`),
          fetch(`${API}/public/clubs/${slug}/players?limit=300`),
        ]);
        if (!clubRes.ok) throw new Error("Club no encontrado");
        const clubData = await clubRes.json();
        const trnData = trnRes.ok ? await trnRes.json() : { tournaments: [] };
        const playersData = playersRes.ok ? await playersRes.json() : { players: [] };
        setData({ ...clubData, tournaments: trnData.tournaments || [] });
        setPlayers(playersData.players || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  // Scroll-spy so the nav underline follows the visible section.
  useEffect(() => {
    if (!data) return;
    const onScroll = () => {
      const y = window.scrollY + 120;
      for (const it of [...NAV_ITEMS].reverse()) {
        const el = document.getElementById(it.k);
        if (el && el.offsetTop <= y) { setActive(it.k); break; }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [data]);

  const scrollTo = (k) => {
    const el = document.getElementById(k);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 74;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: CREAM }}>
        <div className="w-10 h-10 border-4 rounded-full animate-spin"
             style={{ borderColor: NAVY, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6"
           style={{ backgroundColor: CREAM, color: NAVY, fontFamily: FONT_STACK }}>
        <img src={BIRD_WALKER} alt="cadii" className="w-32 opacity-40 mb-6" />
        <h1 className="text-2xl font-bold mb-2">Club no encontrado</h1>
        <p className="opacity-60 mb-6">El club <span className="font-mono">/{slug}</span> no existe o no está activo.</p>
        <Link to="/" className="font-semibold hover:underline" style={{ color: FAIRWAY }}>Volver al inicio</Link>
      </div>
    );
  }

  const club = data.club;
  const rankings = data.rankings || [];
  const tournaments = data.tournaments || [];
  const liveStage = data.live_stage;
  const landing = club.landing || {};
  const showRankings = landing.show_rankings !== false;
  const showTournaments = landing.show_tournaments !== false;
  const showContact = landing.show_contact !== false;
  const bannerUrl = landing.banner_url;

  const liveTournaments = tournaments.filter((t) => ["open", "in_progress"].includes(t.status));
  const isTrnPast = (t) => {
    if (["open", "in_progress"].includes(t.status)) return false;
    return t.status === "completed" || isPastDate(t.end_date || t.start_date);
  };
  const activeTournaments = tournaments.filter((t) => !isTrnPast(t));
  const pastTournaments = tournaments.filter((t) => isTrnPast(t));
  const activeRankings = rankings.filter((r) => r.status !== "completed");
  const pastRankings = rankings.filter((r) => r.status === "completed");
  const shownTournaments = eventsTab === "active" ? activeTournaments : pastTournaments;
  const shownRankings = eventsTab === "active" ? activeRankings : pastRankings;
  const pastCount = pastTournaments.length + pastRankings.length;

  const ogImage = club.logo_url ? (club.logo_url.startsWith("http") ? club.logo_url : `${window.location.origin}${club.logo_url}`) : FALLBACK_LOGO;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = `${origin}/api/public/share/${slug}`;
  const ogTitle = `${club.name} — cadii`;
  const ogDescription = liveStage
    ? `En vivo: ${liveStage.ranking_name} — Etapa ${liveStage.stage_number}: ${liveStage.name}`
    : `Rankings, torneos y jugadores de ${club.name} en cadii`;

  return (
    <div style={{ backgroundColor: CREAM, color: NAVY, fontFamily: FONT_STACK, minHeight: "100vh" }}>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700;800&family=Caveat:wght@500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <TopNav club={club} slug={slug} active={active} onNav={scrollTo} />

      <ClubHero
        club={club}
        slug={slug}
        bannerUrl={bannerUrl}
        liveStage={liveStage}
        shareUrl={shareUrl}
        shareTitle={ogTitle}
        shareText={ogDescription}
      />

      {liveTournaments.length > 0 && (
        <LiveRail tournaments={liveTournaments} liveStage={liveStage} slug={slug} />
      )}

      <main className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <ClubSection club={club} rankings={shownRankings} showRankings={showRankings} slug={slug} eventsTab={eventsTab} setEventsTab={setEventsTab} pastCount={pastCount} />
        <PlayersSection players={players} clubName={club.name} />
        {showTournaments && (
          <TournamentsSection tournaments={shownTournaments} slug={slug} eventsTab={eventsTab} setEventsTab={setEventsTab} pastCount={pastCount} />
        )}
        {showContact && <ContactSection club={club} slug={slug} />}
      </main>

      <SiteFooter />
    </div>
  );
}

/* ─── Nav ─── */
function TopNav({ club, slug, active, onNav }) {
  return (
    <nav
      className="sticky top-0 z-50"
      style={{
        backgroundColor: "rgba(247,242,232,0.92)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: `1px solid rgba(11,29,51,0.08)`,
      }}
      data-testid="club-public-nav"
    >
      <div className="max-w-[1360px] mx-auto flex items-center justify-between px-6 lg:px-10 py-3.5">
        <Link to="/" className="flex items-center gap-3" data-testid="club-nav-home">
          {club.logo_url ? (
            <img
              src={club.logo_url.startsWith("http") ? club.logo_url : `${API.replace(/\/api$/, "")}${club.logo_url}`}
              alt={club.name}
              className="w-10 h-10 rounded-xl object-cover"
              style={{ boxShadow: "0 2px 8px rgba(11,29,51,0.08)" }}
            />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                 style={{ backgroundColor: NAVY }}>
              <span style={{ color: OFF_WHITE, fontFamily: FONT_SCRIPT, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
                {(club.name || "?").charAt(0)}
              </span>
            </div>
          )}
          <div className="hidden sm:flex flex-col leading-tight">
            <span style={{ color: NAVY, fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px" }}>
              {club.name}
            </span>
            <span style={{ color: STONE, fontSize: 10, letterSpacing: "1.6px", fontWeight: 700 }}>
              {(club.city || club.country) ? `${club.city || ""}${club.city && club.country ? " · " : ""}${club.country || ""}`.toUpperCase() : "GOLF CLUB"}
            </span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((it) => (
            <button
              key={it.k}
              onClick={() => onNav(it.k)}
              className="relative py-2"
              style={{ color: NAVY, fontSize: 14, fontWeight: 600, letterSpacing: "-0.1px" }}
              data-testid={`club-nav-${it.k}`}
            >
              {it.label}
              {active === it.k && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 -bottom-0.5"
                  style={{ width: 22, height: 2, backgroundColor: FAIRWAY_ACCENT, borderRadius: 2 }}
                />
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="hidden sm:flex items-baseline gap-1.5 px-3 py-2 rounded-full"
            style={{ color: NAVY }}
            data-testid="club-nav-cadii"
          >
            <span style={{ fontFamily: FONT_SCRIPT, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>cadii</span>
            <span style={{ fontSize: 8, letterSpacing: "1.4px", fontWeight: 800 }}>GOLF CLUB</span>
          </Link>
        </div>
      </div>

      {/* Mobile nav strip */}
      <div className="md:hidden flex items-center gap-1 overflow-x-auto px-4 pb-2"
           style={{ borderTop: "1px solid rgba(11,29,51,0.05)" }}>
        {NAV_ITEMS.map((it) => (
          <button
            key={it.k}
            onClick={() => onNav(it.k)}
            className="rounded-full px-3.5 py-1.5 whitespace-nowrap"
            style={{
              color: active === it.k ? OFF_WHITE : NAVY,
              backgroundColor: active === it.k ? NAVY : "transparent",
              fontSize: 12.5, fontWeight: 700, letterSpacing: "-0.1px",
              flexShrink: 0,
            }}
            data-testid={`club-nav-m-${it.k}`}
          >
            {it.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ─── Hero ─── */
function ClubHero({ club, slug, bannerUrl, liveStage, shareUrl, shareTitle, shareText }) {
  return (
    <section
      id="club"
      className="relative"
      style={{
        backgroundColor: NAVY,
        color: OFF_WHITE,
        backgroundImage: bannerUrl
          ? `linear-gradient(180deg, rgba(11,29,51,0.35) 0%, rgba(11,29,51,0.9) 100%), url(${bannerUrl})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 py-14 sm:py-20">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
          <div className="flex items-end gap-5">
            {club.logo_url ? (
              <img
                src={club.logo_url.startsWith("http") ? club.logo_url : `${API.replace(/\/api$/, "")}${club.logo_url}`}
                alt={club.name}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover"
                style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.35)" }}
              />
            ) : (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl flex items-center justify-center"
                   style={{ backgroundColor: "rgba(244,241,236,0.1)", border: "1px solid rgba(244,241,236,0.2)" }}>
                <span style={{ color: OFF_WHITE, fontFamily: FONT_SCRIPT, fontSize: 60, fontWeight: 700 }}>
                  {(club.name || "?").charAt(0)}
                </span>
              </div>
            )}
            <div>
              <p style={{ color: SUNBURST, fontSize: 12, letterSpacing: "3px", fontWeight: 700, textTransform: "uppercase" }}>
                Club de Golf
              </p>
              <h1 className="mt-1 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]"
                  style={{ letterSpacing: "-1.2px" }}
                  data-testid="public-club-name">
                {club.name}
              </h1>
              {(club.city || club.country) && (
                <p className="mt-3 flex items-center gap-1.5 opacity-80" style={{ fontSize: 15 }}>
                  <MapPin className="w-4 h-4" />
                  {club.city}{club.country ? `, ${club.country}` : ""}
                </p>
              )}
            </div>
          </div>

          <ShareButton
            title={shareTitle}
            text={shareText}
            url={shareUrl}
            variant="pill"
            label="Compartir"
            testId="share-club-btn"
          />
        </div>

        {liveStage && (
          <Link
            to={`/${slug}/rankings/${liveStage.ranking_id}/stages/${liveStage.stage_id}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "rgba(244,241,236,0.10)", border: "1px solid rgba(244,241,236,0.20)" }}
            data-testid="live-banner"
          >
            <span className="flex items-center gap-1.5 text-xs font-black uppercase px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: BURGUNDY, color: OFF_WHITE, letterSpacing: "0.8px" }}>
              <Radio className="w-3 h-3 animate-pulse" /> En vivo
            </span>
            <span className="text-sm opacity-95">
              {liveStage.ranking_name} · Etapa {liveStage.stage_number}: {liveStage.name}
            </span>
            <ChevronRight className="w-4 h-4 opacity-70" />
          </Link>
        )}
      </div>
    </section>
  );
}

/* ─── Live Rail ─── */
function LiveRail({ tournaments, liveStage, slug }) {
  const railRef = useRef(null);
  const scroll = (dir) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };
  return (
    <section
      className="relative"
      style={{
        backgroundColor: NAVY,
        borderTop: `1px solid rgba(244,241,236,0.08)`,
      }}
      data-testid="live-rail"
    >
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 py-8 sm:py-10">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: BURGUNDY }} />
            <h2 style={{ color: OFF_WHITE, fontSize: 13, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
              En vivo ahora
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scroll(-1)}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(244,241,236,0.08)", color: OFF_WHITE }}
              aria-label="Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll(1)}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(244,241,236,0.08)", color: OFF_WHITE }}
              aria-label="Siguiente"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={railRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}
        >
          {tournaments.map((t) => (
            <Link
              key={t.tournament_id}
              to={`/${slug}/tournaments/${t.tournament_id}`}
              className="flex-shrink-0"
              style={{
                width: 320, scrollSnapAlign: "start",
                backgroundColor: OFF_WHITE, color: NAVY,
                borderRadius: 16, padding: 22,
                boxShadow: "0 8px 28px rgba(0,0,0,0.24)",
              }}
              data-testid={`live-card-${t.tournament_id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: BURGUNDY, color: OFF_WHITE, letterSpacing: "1px" }}
                >
                  En vivo
                </span>
                <span
                  className="text-[10px] font-bold uppercase px-2 py-0.5 rounded"
                  style={{ backgroundColor: "rgba(46,72,52,0.10)", color: FAIRWAY, letterSpacing: "0.6px" }}
                >
                  {t.mode === "net" ? `NET ${t.hcp_percent || ""}%` : "GROSS"}
                </span>
              </div>
              <h3 className="font-black" style={{ fontSize: 20, letterSpacing: "-0.3px", lineHeight: 1.15 }}>
                {t.name}
              </h3>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px]" style={{ color: STONE }}>
                {t.course_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.course_name}</span>}
                {t.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{t.start_date}</span>}
              </div>
              <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: "1px solid rgba(11,29,51,0.08)" }}>
                <div className="flex items-center gap-1.5" style={{ color: NAVY }}>
                  <Users className="w-4 h-4" />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{t.player_count || 0} jugadores</span>
                </div>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </div>
            </Link>
          ))}
          {liveStage && (
            <Link
              to={`/${slug}/rankings/${liveStage.ranking_id}/stages/${liveStage.stage_id}`}
              className="flex-shrink-0"
              style={{
                width: 320, scrollSnapAlign: "start",
                backgroundColor: SUNBURST, color: NAVY,
                borderRadius: 16, padding: 22,
                boxShadow: "0 8px 28px rgba(0,0,0,0.24)",
              }}
              data-testid={`live-card-stage-${liveStage.stage_id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: NAVY, color: SUNBURST, letterSpacing: "1px" }}
                >
                  Ranking · Etapa
                </span>
              </div>
              <h3 className="font-black" style={{ fontSize: 20, letterSpacing: "-0.3px", lineHeight: 1.15 }}>
                {liveStage.name}
              </h3>
              <p className="mt-2" style={{ fontSize: 13, opacity: 0.75, fontWeight: 600 }}>
                {liveStage.ranking_name}
              </p>
              <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: "1px solid rgba(11,29,51,0.18)" }}>
                <div className="flex items-center gap-1.5">
                  <Trophy className="w-4 h-4" />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>Ver leaderboard</span>
                </div>
                <ChevronRight className="w-4 h-4" />
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─── Club (about + rankings) ─── */
function ClubSection({ club, rankings, showRankings, slug, eventsTab, setEventsTab, pastCount }) {
  return (
    <section id="club-info" className="pt-14 pb-6">
      <SectionEyebrow>Rankings y competencias</SectionEyebrow>
      <EventsTabs tab={eventsTab} setTab={setEventsTab} pastCount={pastCount} />
      {showRankings && rankings.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="public-rankings-grid">
          {rankings.map((r) => {
            const st = STATUS_MAP[r.status] || STATUS_MAP.active;
            return (
              <Link
                key={r.ranking_id}
                to={`/${slug}/rankings/${r.ranking_id}`}
                className="block group"
                data-testid={`public-ranking-${r.ranking_id}`}
              >
                <div
                  className="p-6 transition-all"
                  style={{
                    backgroundColor: OFF_WHITE,
                    borderRadius: 16,
                    border: "1px solid rgba(11,29,51,0.06)",
                  }}
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: "rgba(46,72,52,0.10)" }}
                      >
                        <Trophy className="w-5 h-5" style={{ color: FAIRWAY }} strokeWidth={1.6} />
                      </div>
                      <div>
                        <h3 className="font-black" style={{ fontSize: 17, letterSpacing: "-0.2px" }}>{r.name}</h3>
                        <span
                          className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase"
                          style={{ backgroundColor: st.bg, color: st.color, letterSpacing: "0.6px" }}
                        >
                          {st.label}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-40 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  <div className="flex flex-wrap gap-4 text-[13px]" style={{ color: STONE }}>
                    {r.season && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{r.season}</span>}
                    <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{r.completed_stages || 0}/{r.stage_count || 0} etapas</span>
                  </div>
                  {r.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {r.categories.map((c) => (
                        <span
                          key={c.category_id}
                          className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: "rgba(46,72,52,0.08)", color: FAIRWAY }}
                        >
                          {c.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <EmptyBlock icon={Trophy}>{eventsTab === "past" ? "No hay rankings finalizados." : "No hay rankings activos en este momento."}</EmptyBlock>
      )}
    </section>
  );
}

/* ─── Jugadores ─── */
function PlayersSection({ players, clubName }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? players : players.slice(0, 12);
  return (
    <section id="jugadores" className="pt-14 pb-6">
      <SectionEyebrow>Nuestros jugadores</SectionEyebrow>
      <div className="flex items-baseline justify-between mb-6">
        <h2 style={{ fontSize: 15, color: STONE, fontWeight: 600 }}>
          {players.length > 0
            ? <>Padrón activo · <span style={{ color: NAVY, fontWeight: 800 }}>{players.length}</span> jugadores</>
            : `Aún no hay jugadores publicados por ${clubName}`}
        </h2>
      </div>

      {players.length === 0 ? (
        <EmptyBlock icon={Users}>El club aún no publica su padrón.</EmptyBlock>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="public-players-grid">
            {visible.map((p) => {
              const parts = (p.name || "").split(" ").filter(Boolean);
              const initials = parts.length >= 2
                ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
                : (p.name || "?").slice(0, 2).toUpperCase();
              const hcp = p.handicap_index;
              return (
                <div
                  key={p.member_id}
                  className="p-3 flex items-center gap-3"
                  style={{
                    backgroundColor: OFF_WHITE,
                    borderRadius: 12,
                    border: "1px solid rgba(11,29,51,0.06)",
                  }}
                  data-testid={`public-player-${p.member_id}`}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: NAVY, color: OFF_WHITE, fontWeight: 800, fontSize: 13, letterSpacing: "0.5px" }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate" style={{ fontSize: 13, fontWeight: 700, color: NAVY, letterSpacing: "-0.1px" }}>
                      {p.name}
                    </p>
                    <p className="truncate" style={{ fontSize: 11.5, color: STONE, fontWeight: 600 }}>
                      {typeof hcp === "number" ? `HCP ${hcp.toFixed(1)}` : "HCP —"}
                      {p.is_senior ? " · Senior" : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {players.length > 12 && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="px-5 py-2.5 rounded-full font-bold text-sm"
                style={{
                  backgroundColor: showAll ? "rgba(11,29,51,0.06)" : NAVY,
                  color: showAll ? NAVY : OFF_WHITE,
                  letterSpacing: "-0.1px",
                }}
                data-testid="public-players-toggle"
              >
                {showAll ? "Ver menos" : `Ver todos (${players.length})`}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ─── Torneos ─── */
function TournamentsSection({ tournaments, slug, eventsTab, setEventsTab, pastCount }) {
  const all = tournaments;
  return (
    <section id="torneos" className="pt-14 pb-6">
      <SectionEyebrow>Torneos</SectionEyebrow>
      <EventsTabs tab={eventsTab} setTab={setEventsTab} pastCount={pastCount} />
      {all.length === 0 ? (
        <EmptyBlock icon={Medal}>{eventsTab === "past" ? "No hay torneos finalizados." : "Todavía no hay torneos activos o próximos."}</EmptyBlock>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="public-tournaments-grid">
          {all.map((t) => {
            const trnLive = ["open", "in_progress"].includes(t.status);
            return (
              <Link
                key={t.tournament_id}
                to={`/${slug}/tournaments/${t.tournament_id}`}
                className="block group"
                data-testid={`public-tournament-${t.tournament_id}`}
              >
                <div
                  className="p-6"
                  style={{
                    backgroundColor: OFF_WHITE,
                    borderRadius: 16,
                    border: "1px solid rgba(11,29,51,0.06)",
                  }}
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: "rgba(46,72,52,0.10)" }}
                      >
                        <Medal className="w-5 h-5" style={{ color: FAIRWAY }} strokeWidth={1.6} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-black truncate" style={{ fontSize: 17, letterSpacing: "-0.2px" }}>{t.name}</h3>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span
                            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: "rgba(46,72,52,0.10)", color: FAIRWAY, letterSpacing: "0.6px" }}
                          >
                            {t.mode === "net" ? `NET ${t.hcp_percent || ""}%` : "GROSS"}
                          </span>
                          {trnLive && (
                            <span
                              className="flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: BURGUNDY, color: OFF_WHITE, letterSpacing: "0.8px" }}
                            >
                              <Radio className="w-2.5 h-2.5" /> En vivo
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-40 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  <div className="flex flex-wrap gap-4 text-[13px]" style={{ color: STONE }}>
                    {t.course_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.course_name}</span>}
                    {t.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{t.start_date}</span>}
                    <span>{t.rounds} ronda{t.rounds > 1 ? "s" : ""}</span>
                    <span>{t.player_count || 0} jugadores</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ─── Contacto ─── */
function ContactSection({ club, slug }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState({ sending: false, sent: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    if (state.sending) return;
    setState({ sending: true, sent: false, error: null });
    try {
      const res = await fetch(`${API}/public/clubs/${slug}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "No se pudo enviar" }));
        throw new Error(err.detail || "No se pudo enviar");
      }
      setState({ sending: false, sent: true, error: null });
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      setState({ sending: false, sent: false, error: e.message });
    }
  };

  return (
    <section id="contacto" className="pt-14 pb-16">
      <SectionEyebrow>Contacto</SectionEyebrow>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Info card */}
        <div
          className="p-7"
          style={{
            backgroundColor: NAVY,
            color: OFF_WHITE,
            borderRadius: 16,
          }}
          data-testid="contact-info-card"
        >
          <p style={{ color: SUNBURST, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
            Hablemos
          </p>
          <h3 className="mt-2 font-black" style={{ fontSize: 26, letterSpacing: "-0.6px", lineHeight: 1.15 }}>
            {club.name}
          </h3>
          <div className="mt-6 space-y-3">
            {club.email && (
              <a href={`mailto:${club.email}`} className="flex items-center gap-3 group">
                <span className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(244,241,236,0.08)" }}>
                  <Mail className="w-4 h-4" style={{ color: SUNBURST }} />
                </span>
                <span style={{ fontSize: 14, fontWeight: 600 }} className="group-hover:underline">{club.email}</span>
              </a>
            )}
            {club.phone && (
              <a href={`tel:${club.phone}`} className="flex items-center gap-3 group">
                <span className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(244,241,236,0.08)" }}>
                  <Phone className="w-4 h-4" style={{ color: SUNBURST }} />
                </span>
                <span style={{ fontSize: 14, fontWeight: 600 }} className="group-hover:underline">{club.phone}</span>
              </a>
            )}
            {(club.city || club.country) && (
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(244,241,236,0.08)" }}>
                  <MapPin className="w-4 h-4" style={{ color: SUNBURST }} />
                </span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{club.city}{club.country ? `, ${club.country}` : ""}</span>
              </div>
            )}
            {!club.email && !club.phone && (
              <p style={{ color: "rgba(244,241,236,0.6)", fontSize: 13 }}>
                Sin datos de contacto publicados aún.
              </p>
            )}
          </div>

          <div className="mt-8 pt-6" style={{ borderTop: "1px solid rgba(244,241,236,0.15)" }}>
            <p style={{ color: "rgba(244,241,236,0.55)", fontSize: 11, letterSpacing: "1.6px", fontWeight: 700, textTransform: "uppercase" }}>
              Sitio impulsado por
            </p>
            <Link to="/" className="inline-flex items-baseline gap-1.5 mt-2">
              <span style={{ fontFamily: FONT_SCRIPT, fontSize: 34, fontWeight: 700, color: OFF_WHITE, lineHeight: 1 }}>cadii</span>
              <span style={{ fontSize: 9, letterSpacing: "1.6px", color: OFF_WHITE, fontWeight: 800 }}>GOLF<br />CLUB</span>
            </Link>
          </div>
        </div>

        {/* Contact form */}
        <form
          onSubmit={submit}
          className="p-7"
          style={{
            backgroundColor: OFF_WHITE,
            borderRadius: 16,
            border: "1px solid rgba(11,29,51,0.06)",
          }}
          data-testid="contact-form"
        >
          <p style={{ color: FAIRWAY, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
            Escríbenos
          </p>
          <h3 className="mt-2 font-black" style={{ fontSize: 22, letterSpacing: "-0.5px" }}>
            Envíanos un mensaje
          </h3>

          <div className="mt-5 space-y-3">
            <FormField
              label="Nombre"
              value={form.name}
              onChange={(v) => setForm((s) => ({ ...s, name: v }))}
              placeholder="Tu nombre"
              testId="contact-name"
              required
            />
            <FormField
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => setForm((s) => ({ ...s, email: v }))}
              placeholder="tu@correo.com"
              testId="contact-email"
              required
            />
            <FormField
              label="Mensaje"
              value={form.message}
              onChange={(v) => setForm((s) => ({ ...s, message: v }))}
              placeholder="Cuéntanos en qué te podemos ayudar…"
              testId="contact-message"
              textarea
              required
            />
          </div>

          {state.error && (
            <p className="mt-3 text-sm" style={{ color: BURGUNDY }}>{state.error}</p>
          )}
          {state.sent && (
            <p className="mt-3 text-sm" style={{ color: FAIRWAY, fontWeight: 700 }}>
              ¡Mensaje enviado! El club te contactará pronto.
            </p>
          )}

          <button
            type="submit"
            disabled={state.sending}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 font-black text-sm"
            style={{
              backgroundColor: state.sending ? "rgba(11,29,51,0.5)" : NAVY,
              color: OFF_WHITE,
              letterSpacing: "-0.1px",
            }}
            data-testid="contact-submit"
          >
            <Send className="w-4 h-4" />
            {state.sending ? "Enviando…" : "Enviar mensaje"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function SiteFooter() {
  return (
    <footer className="py-10 px-6" style={{ backgroundColor: NAVY, color: OFF_WHITE }} data-testid="site-footer">
      <div className="max-w-[1180px] mx-auto flex flex-col items-center gap-5">
        <Link to="/" className="inline-flex items-baseline gap-1.5">
          <span style={{ fontFamily: FONT_SCRIPT, fontSize: 34, fontWeight: 700, lineHeight: 1 }}>cadii</span>
          <span style={{ fontSize: 9, letterSpacing: "1.6px", fontWeight: 800 }}>GOLF<br />CLUB</span>
        </Link>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs" style={{ color: "rgba(244,241,236,0.7)" }}>
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          <span aria-hidden>·</span>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
        <p
          className="text-center"
          style={{ color: "rgba(244,241,236,0.65)", fontSize: 12, letterSpacing: 0.2, lineHeight: 1.5, maxWidth: 720 }}
          data-testid="legal-entity-line"
        >
          CADii es operado por <strong style={{ color: OFF_WHITE }}>Multigoods Inmuebles, S. de R.L. de C.V.</strong>
          <br className="hidden sm:block" />
          CADii is operated by <strong style={{ color: OFF_WHITE }}>Multigoods Inmuebles, S. de R.L. de C.V.</strong>
        </p>
        <p style={{ color: "rgba(244,241,236,0.45)", fontSize: 11, letterSpacing: 0.4 }}>
          © {new Date().getFullYear()} Multigoods Inmuebles, S. de R.L. de C.V. · Todos los derechos reservados
        </p>
        <Link
          to="/club/login"
          className="inline-block hover:text-white transition-colors"
          style={{ color: "rgba(244,241,236,0.35)", fontSize: 10, letterSpacing: 1 }}
          data-testid="master-login-link"
        >
          mastercadii
        </Link>
      </div>
    </footer>
  );
}

/* ─── Building blocks ─── */
function SectionEyebrow({ children }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="inline-block w-6 h-px" style={{ backgroundColor: FAIRWAY }} />
      <span style={{ color: FAIRWAY, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
        {children}
      </span>
    </div>
  );
}

function EmptyBlock({ icon: Icon, children }) {
  return (
    <div
      className="py-14 text-center rounded-2xl"
      style={{ backgroundColor: OFF_WHITE, border: "1px dashed rgba(11,29,51,0.15)" }}
    >
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: NAVY }} strokeWidth={1.5} />
      <p style={{ color: STONE, fontSize: 14, fontWeight: 600 }}>{children}</p>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text", textarea = false, required = false, testId }) {
  const commonStyle = {
    width: "100%",
    backgroundColor: CREAM,
    border: "1px solid rgba(11,29,51,0.10)",
    borderRadius: 10,
    padding: "10px 14px",
    color: NAVY,
    fontSize: 14,
    fontFamily: FONT_STACK,
    outline: "none",
  };
  return (
    <label className="block">
      <span style={{ fontSize: 11, letterSpacing: "1.4px", fontWeight: 800, color: STONE, textTransform: "uppercase" }}>
        {label}
      </span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          rows={4}
          data-testid={testId}
          style={{ ...commonStyle, marginTop: 6, resize: "vertical", minHeight: 96 }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          style={{ ...commonStyle, marginTop: 6 }}
        />
      )}
    </label>
  );
}
